import type { Icon } from '@/shared/registry'
import type { ReactNode } from 'react'

import { cn, focusRing } from '@/shared/ui/cn'

/**
 * A page's header band, from the mockups' `components/details/DetailsHeader.jsx`: an icon tile,
 * a small uppercase eyebrow, the page's one `h1`, and secondary actions on the right. It sits
 * first inside `PageContainer` and cancels its top padding, so the band meets the nav bar.
 *
 * The band is the page background over a bottom divider, as the mockup has it. The mockup's tile
 * glow has no theme token yet, so the tile takes the card shadow.
 * The heading takes focus after each navigation (`AppShell`).
 */
export function PageHeader(props: {
  readonly icon: Icon
  readonly title: string
  readonly eyebrow?: string
  /** A muted line under the title, such as a record's status. */
  readonly subtitle?: ReactNode
  readonly actions?: ReactNode
}) {
  const { icon: Icon } = props

  return (
    <div className="relative isolate -mx-4 -mt-8 mb-8 flex flex-wrap items-start justify-between gap-3 px-4 pb-7 pt-8 sm:-mx-6 sm:px-6 lg:-mt-10 lg:pt-10">
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-1/2 -z-10 w-screen -translate-x-1/2 border-b border-divider bg-background"
      />
      <div className="flex min-w-0 items-center gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-primary-ring bg-gradient-to-br from-primary-soft to-accent-light text-primary-main shadow-card">
          <Icon size={24} />
        </span>
        <div className="min-w-0">
          {props.eyebrow !== undefined && (
            <p className="text-xs font-normal uppercase tracking-[0.18em] text-text-secondary">
              {props.eyebrow}
            </p>
          )}
          <h1
            tabIndex={-1}
            className={cn(
              'font-heading text-2xl font-medium leading-[1.2] text-text-primary sm:text-[2rem]',
              focusRing,
            )}
          >
            {props.title}
          </h1>
          {props.subtitle !== undefined && (
            <p className="mt-1 text-sm text-text-secondary">{props.subtitle}</p>
          )}
        </div>
      </div>
      {/*
        The actions drop under the title when the row is too narrow, and a button there may shrink
        and wrap its label rather than run past the edge at 320 px. The theme keeps every button
        from shrinking, so that rule is overridden here, important to win over MUI's styles.
      */}
      {props.actions !== undefined && (
        <div className="min-w-0 max-w-full self-center [&_.MuiButton-root]:max-w-full [&_.MuiButton-root]:shrink!">
          {props.actions}
        </div>
      )}
    </div>
  )
}
